import * as AccountApi from 'Agent/Accounts/api';
import {ReportRequestDto} from 'Agent/Accounts/api';
import {ValidEnrollmentApplication} from 'Agent/Accounts/models/EnrollmentApplication';
import {AsyncOperation, AsyncResult} from 'Core/models/Async';
import {getAxiosConfig, getUrlFromUri, handleResponse, withCancellation} from 'Shared/utilities/fetchHelpers';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {AxiosResponse} from 'axios';
import {DiscrepancySummaryFilter} from 'Agent/Accounts/ducks/discrepancySummary';
import AccountRouteConstants from '../AccountRouteConstants';
import {CustomerConnectionProfile} from '../ducks/a360EnrollmentApplication';

const reportsApi = new AccountApi.ReportsApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const enrollmentApplicationApi = new AccountApi.EnrollmentApplicationApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);
const discrepancySummariesApi = new AccountApi.DiscrepancySummariesApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);

export const accountsRootUri = 'api/employer/v1/accounts';
const customerRootUri = 'api/employer/v1/access360Enrollments';
const getAxiosConfigAndHeaders = () => {
  const axiosConfig = getAxiosConfig();
  return {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
    },
  };
};

export default {
  fetchCustomerConnectionProfile: async (customerUri: string): Promise<AsyncResult<CustomerConnectionProfile[]>> => {
    const apiUrl = 'api/employer/v1/access360Enrollments/getConnectionProfile';
    const requestPayload = {
      'customerUri': customerUri
    };

    const response: AxiosResponse<CustomerConnectionProfile[], any> = await axiosInstance.post(
      apiUrl, requestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  uploadA360EnrollmentApplication: async (accountNumber: string, conProfileNo: string, file: File) => {
    const apiUrl = 'api/documents/v1/enrollmentapplications/upload';
    const formData = new FormData();
    formData.append('connectionProfileNumber', conProfileNo);
    formData.append('file', file, file.name);
    formData.append('eisFlag', 'true');
    formData.append('accountNumber', accountNumber);
    const response: AxiosResponse<string> = await axiosInstance.post(apiUrl, formData, getAxiosConfig());
    return handleResponse(response);
  },

  uploadEnrollmentApplication: (
    accountId: string,
    accountNumber: string,
    agentNumber: string,
    application: ValidEnrollmentApplication,
    encryptedIdentifier: string
  ) =>
    enrollmentApplicationApi
      .enrollmentApplicationUploadDocument(
        accountId,
        '1',
        accountNumber,
        agentNumber,
        application.enrollmentType,
        application.file,
        {
          params: {encryptedIdentifier},
          ...getAxiosConfig(),
        }
      )
      .then(handleResponse),
  requestReport: (
    accountNumber: string,
    portalUserId: string,
    email: string,
    sourceId: string,
    encryptedIdentifier: string,
    eisFlag?: boolean
  ) =>
    withCancellation(cancelToken =>
      reportsApi
        .reportsRequestReport(
          '1',
          {accountNumber, portalUserId, email, sourceId, eisFlag},
          {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)}
        )
        .then(handleResponse)
    ),
  getAccountDetails: (
    bodyParams: URLSearchParams
  ) =>
    {
      const apiUrl = AccountRouteConstants.ACCOUNT_DETAILS_API('1');
      const axiosConfig = getAxiosConfig();
      return axiosInstance
              .post(getUrlFromUri(`${apiUrl}`), bodyParams , {
                ...axiosConfig,
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  ...axiosConfig.headers,
                },
              })
              .then(handleResponse);
    },
    getAccounts: (
      bodyParams?: URLSearchParams
    ) =>
          {
            const apiUrl = AccountRouteConstants.ACCOUNTS_API('1');
            const axiosConfig = getAxiosConfig();
            return axiosInstance
              .post(getUrlFromUri(`${apiUrl}`), bodyParams , {
                ...axiosConfig,
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  ...axiosConfig.headers,
                },
              })
              .then(handleResponse);
          },
  getPolicyInformationReports: (encryptedIdentifier: string, userId: string) =>
    withCancellation(cancelToken =>
      reportsApi.reportsGet(userId, '1', false, {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)})
    ).then(handleResponse) as AsyncOperation<ReportRequestDto[]>,
  deletePolicyInformationReport: (
    encryptedIdentifier: string,
    userId: string,
    reportId: number,
    eisFlag: boolean | null
  ) =>
    withCancellation(cancelToken =>
      reportsApi.reportsRemoveReport(
        '1',
        {portalUserId: userId, reportId, eisFlag},
        {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)}
      )
    ).then(handleResponse),
  getDiscrepancySummaries: (
    encryptedIdentifier: string,
    accountNumber: string,
    sourceId: number,
    paymentDate: Date,
    discrepancySummaryFilter: DiscrepancySummaryFilter
  ) =>
    withCancellation(cancelToken =>
      discrepancySummariesApi
        .discrepancySummariesGet('1', {
          params: {
            encryptedIdentifier,
            accountNumber,
            sourceId,
            paymentDate: paymentDate.toISOString(),
            ...discrepancySummaryFilter,
          },
          ...getAxiosConfig(cancelToken),
        })
        .then(handleResponse)
    ),
  getImages: (encryptedIdentifier: string, accountId: string, eisFlag: boolean, isGroup: boolean) => {
    const apiUrl = `api/employer/v1/accounts/${accountId}/images?encryptedIdentifier=${encryptedIdentifier}&eisFlag=${eisFlag}&isGroup=${isGroup}`;
    const axiosConfig = getAxiosConfig();
    return axiosInstance
      .get(getUrlFromUri(`${apiUrl}`), {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
        },
      })
      .then(handleResponse);
  },

  getInvoiceDiscrepancies: (accountNumber: string, invoiceLink: string) => {
    const apiUrl = '/api/employer/v1/invoice/discrepancies';
    const axiosConfig = getAxiosConfig();
    return axiosInstance
      .post(
        getUrlFromUri(`${apiUrl}`),
        {accountNumber, invoiceLink},
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
          },
        }
      )
      .then(handleResponse);
  },
};
